import axios from 'axios'
import Env from '@finx/web-repository/src/services/Env'

// Enum que representa as estratégias conhecidas e seus valores inteiros esperados pelo backend.
export enum TokenExchangeStrategy {
  DASA = 1,
}

// Mapeamento de identificador (string) para value do enum.
const IDENTIFICADOR_TO_STRATEGY: Record<string, number> = {
  'dasa': TokenExchangeStrategy.DASA,
}

function mapIdentificadorToStrategyId(identificador?: string | number): number {
 
  if (typeof identificador === 'number') return identificador
  const idStr = String(identificador).trim().toLowerCase()
  // se for dígitos, parseie
  if (/^\d+$/.test(idStr)) return Number(idStr)
  // senão tente mapear via enum lookup
  if (IDENTIFICADOR_TO_STRATEGY[idStr] !== undefined) return IDENTIFICADOR_TO_STRATEGY[idStr]
  // se não reconhecido, lance erro para evitar enviar string não suportada ao backend
  throw new Error(`Identificador desconhecido: ${identificador}`)
}

/**
 * Serviço para trocar UUID -> token Dasa -> token Keycloak (token exchange).
 * Inputs:
 *  - uuid: string recebido do frontend
 *  - identificador: string ou number que indica a estratégia (ex: 'dasa', 'keycloak')
 *  - cfg (opcional): sobrescreve URLs/credenciais via parâmetros; se não informado, usa Env (window)
 * Outputs:
 *  - objeto com { access_token, expires_in, refresh_token?, raw?: any }
 */
export default {
  async exchange(uuid: string, identificador?: string | number) {
    if (!uuid) throw new Error('uuid is required')

    // Payload exigido pelo backend: { strategyId: number, object: string }
    const backendUrl = (Env as any).API_TOKEN_EXCHANGE || (window as any).API_TOKEN_EXCHANGE

    // Map the identificador to a strategy ID
    const strategyId = mapIdentificadorToStrategyId(identificador)
    
    let backendResponse
    try {
      // log básico da chamada (não expondo token)
      // eslint-disable-next-line no-console
      backendResponse = await axios.post(
        backendUrl,
        { strategyId, object: uuid },
        { headers: { 'Content-Type': 'application/json' } }
      )
      // log keys of response data to help debugging without printing tokens
      const respData = backendResponse && (backendResponse as any).data
      const safeInfo = respData && typeof respData === 'object' ? Object.keys(respData) : typeof respData
      // eslint-disable-next-line no-console
    } catch (err) {
      const anyErr = err as any
      // Monta mensagem de erro resiliente para depuração (evita '[object Object] is not valid JSON')
      const status = anyErr && anyErr.response && anyErr.response.status
      const data = anyErr && anyErr.response && anyErr.response.data
      const message = anyErr && anyErr.message
      // eslint-disable-next-line no-console
      let details: string
      try {
        details = JSON.stringify({ status, data, message })
      } catch (e) {
        // fallback para evitar exceção ao serializar
        details = String({ status, data, message })
      }
      throw new Error(`Erro ao solicitar token-exchange no backend: ${details}`)
    }

    const kcData = (backendResponse && (backendResponse as any).data) || {}

    // Support two response shapes:
    // 1) backend returns camelCase: { accessToken, refreshToken, expiresIn, tokenType }
    // 2) backend returns OAuth-like snake_case: { access_token, refresh_token, expires_in, token_type }
    const accessToken = kcData.accessToken || kcData.access_token
    const refreshToken = kcData.refreshToken || kcData.refresh_token
    const expiresIn = kcData.expiresIn || kcData.expires_in
    const tokenType = kcData.tokenType || kcData.token_type || 'Bearer'

    if (!accessToken) {
      throw new Error('O endpoint de token-exchange não retornou accessToken')
    }

    // Normalize to the app's expected shape (access_token, refresh_token, expires_in, token_type)
    return {
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_in: expiresIn,
      token_type: tokenType,
      raw: kcData,
    }
  },
}